<template>
    <div class="w-full h-full">
        <div
            v-if="maintenanceModeShow"
            ref="maintenanceModeRef"
        >
            <MaintenanceMode />
        </div>

        <div class="flex h-full w-full relative">
            <AppBar
                :mini="mini"
                :showDownloadPopup="showDownloadPopup"
                :marginClass="marginClass"
                @showSideBars="showSideBars"
                @changeShowDownloadPopup="changeShowDownloadPopup"
            />

            <SideBarWrapper
                v-model:mini="mini"
                :showMobileSideBars="showMobileSideBars"
                :withBottomPadding="!isSettingsRoute"
                :maintenanceModeHeight="isSettingsRoute ? 0 : customHeight"
                @swipeLeft="hideSideBars"
            >
                <SettingsSideBar
                    v-if="isSettingsRoute"
                    :mini="mini"
                />

                <MainSideBar
                    v-else
                    :mini="mini"
                    :showMobileSideBars="showMobileSideBars"
                />
            </SideBarWrapper>

            <div
                v-if="showMobileSideBars"
                class="fixed inset-0 z-90 min-mb:hidden"
                @click="hideSideBars"
            ></div>

            <div
                class="bg-white dark:bg-blue-800 w-full overflow-hidden transition-spacing"
                :class="marginClass"
                @click="hideSideBars"
            >
                <div
                    class="relative w-full transition-all"
                    :class="mainClass"
                >
                    <router-view v-slot="{ Component, route }">
                        <transition name="fade">
                            <div
                                class="h-full transition-all"
                                :class="[appStore.aiAgentOpen && !appStore.aiAgentOnWindow ? 'max-mb:w-main-layout w-main-layout-with-ai' : 'w-main-layout']"
                                :key="route.name"
                            >
                                <component :is="Component" />
                            </div>
                        </transition>
                    </router-view>

                    <transition name="fade">
                        <div
                            v-if="routeIsLoading"
                            class="absolute top-0 w-full h-full flex items-center justify-center z-120 bg-black/4"
                        >
                            <MainLoader />
                        </div>
                    </transition>
                </div>
            </div>

            <DraggableDownloadList
                :showDownloadPopup="showDownloadPopup"
                @changeShowDownloadPopup="changeShowDownloadPopup"
            />

            <LaravelReverb
                v-if="userId"
                :key="`${userId}-${workspaceSlug}`"
            />
        </div>
    </div>
</template>

<script setup>
import {
    ref, computed, provide, onMounted, watch, inject, onBeforeMount, onUnmounted, useTemplateRef,
} from 'vue'
import { useRoute } from 'vue-router'

import SideBarWrapper from '@/components/navigation/SideBar/SideBarWrapper.vue'
import MainSideBar from '@/components/navigation/SideBar/MainSideBar.vue'
import SettingsSideBar from '@/components/navigation/SettingsSideBar/SettingsSideBar.vue'
import AppBar from '@/components/navigation/AppBar.vue'
import MainLoader from '@/components/mainLoader/MainLoader.vue'
import DraggableDownloadList from '@/components/download/DraggableDownloadList.vue'
import MaintenanceMode from '@/components/maintenanceMode/MaintenanceMode.vue'
import LaravelReverb from '@/components/notifications/LaravelReverb.vue'

import { getBarsPositions, setBarsPositions, addEventListenerStorage } from '@/helpers/app-local-storage-helper'
import {
    addFeaturebaseScript, initFeaturebase, initFeaturebaseSurvey, initFeaturebaseChangelog,
} from '@/helpers/featurebase-helper'

import { useAuthorizationStore } from '@/store/authorizationStore'
import { useBreadCrumbsStore } from '@/store/breadCrumbsStore'
import { useAppStore } from '@/store/appStore'
import { useDownloadReportingStore } from '@/store/download/downloadReportingStore'
import { useDownloadLeadsStore } from '@/store/download/downloadLeadsStore'

import { viewPorts } from '@/constants/viewPorts'
import { MAINTENANCE_MODE_STATUS } from '@/constants/maintenanceMode'
import { SETTINGS_ROUTE_REFERENCES } from '@/constants/settingsSidebarMenu'

const route = useRoute()

const breadCrumbsStore = useBreadCrumbsStore()
const appStore = useAppStore()
const authorizationStore = useAuthorizationStore()
const downloadReportingStore = useDownloadReportingStore()
const downloadLeadsStore = useDownloadLeadsStore()

const currentViewSize = inject('currentViewSize')

const maintenanceModeRef = useTemplateRef('maintenanceModeRef')

const mini = ref(false)

const showDownloadPopup = ref(true)

const showMobileSideBars = ref(false)

provide('mini', mini)
provide('showMobileSideBars', showMobileSideBars)

const isMbView = computed(() => viewPorts.mb >= currentViewSize.value)

const isAccountTrial = computed(() => authorizationStore.isAccountTrial)
const isRequiredUserTwoFactor = computed(() => authorizationStore.isRequiredUserTwoFactor)
const isGracePeriodUserTwoFactor = computed(() => authorizationStore.isGracePeriodUserTwoFactor)

const userId = computed(() => authorizationStore.getUserId)
const workspaceSlug = computed(() => authorizationStore.getWorkspaceSlug)

const maintenanceModeShow = computed(() => appStore.maintenanceMode === MAINTENANCE_MODE_STATUS.active)

const customHeight = computed(() => {
    if (maintenanceModeShow.value && maintenanceModeRef.value) {
        return maintenanceModeRef.value.clientHeight
    }

    return 0
})

const isSettingsRoute = computed(() => SETTINGS_ROUTE_REFERENCES.includes(route.meta.reference))

const marginClass = computed(() => [
    mini.value && 'min-mb:ml-from-side-bars-min',
    !mini.value && 'min-mb:ml-from-side-bars-max',
])

const mainClass = computed(() => {
    if (isAccountTrial.value) {
        return breadCrumbsStore.showMobileBC && isMbView
            ? 'mt-from-app-bar-with-bc-trial h-main-container-with-bc-trial max-mob:mt-from-app-bar-with-bc-trial-mobile max-mob:h-main-container-with-bc-trial-mobile'
            : 'mt-from-app-bar-with-trial h-main-container-with-trial max-mob:mt-from-app-bar-with-trial-mobile max-mob:h-main-container-with-trial-mobile'
    }

    return breadCrumbsStore.showMobileBC && isMbView
        ? 'mt-from-app-bar-with-bc h-main-container-with-bc max-mob:mt-from-app-bar-with-bc-mobile max-mob:h-main-container-with-bc-mobile'
        : 'mt-from-app-bar h-main-container max-mob:mt-from-app-bar-mobile max-mob:h-main-container-mobile'
})

const routeIsLoading = computed(() => appStore.routeIsLoading)

onBeforeMount(() => {
    addFeaturebaseScript()
})

onMounted(() => {
    getSideBarsPositions()
    addEventListenerStorage()

    initFeaturebase()
    initFeaturebaseSurvey()
    initFeaturebaseChangelog()
})

function getSideBarsPositions() {
    const positions = getBarsPositions()

    mini.value = positions.mini
}

function showSideBars() {
    mini.value = false

    showMobileSideBars.value = true
}

function changeShowDownloadPopup() {
    showDownloadPopup.value = !showDownloadPopup.value
}

function hideSideBars() {
    showMobileSideBars.value = false
}

watch(() => ({ mini: mini.value }), (positions) => {
    setBarsPositions(positions)
})

watch(route, () => {
    hideSideBars()
})

watch(showMobileSideBars, (value) => {
    if (value && appStore.aiAgentOpen) {
        appStore.toggleAiAgent()
    }
}, { immediate: true })

onUnmounted(() => {
    downloadReportingStore.$reset()
    downloadLeadsStore.resetTimer()
    downloadLeadsStore.$reset()
})
</script>
